import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  Send, 
  Search, 
  User,
  ArrowLeft,
  Plus,
  Check,
  CheckCheck,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Messages() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, loading: authLoading } = useAuth();
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [initialMessage, setInitialMessage] = useState('');
  
  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/connexion');
    }
  }, [user, authLoading, navigate]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId) => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Erreur lors du chargement des messages');
    }
  }, [token]);

  // Initial load and polling
  useEffect(() => {
    if (token) {
      fetchConversations();
      
      // Poll for new messages every 5 seconds
      pollingRef.current = setInterval(() => {
        fetchConversations();
        if (selectedConversation) {
          fetchMessages(selectedConversation.id);
        }
      }, 5000);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [token, fetchConversations, fetchMessages, selectedConversation]);

  // Handle URL params for starting a conversation
  useEffect(() => {
    const recipientId = searchParams.get('to');
    const recipientName = searchParams.get('name');
    const conversationId = searchParams.get('conversation');
    
    if (recipientId && recipientName && user) {
      setShowNewConversation(true);
      setSearchQuery(recipientName);
      setSearchResults([{ id: recipientId, name: recipientName }]);
    }
    
    // Auto-select conversation if ID is provided
    if (conversationId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === conversationId);
      if (conv) {
        handleSelectConversation(conv);
      }
    }
  }, [searchParams, user, conversations]);

  // Select a conversation
  const handleSelectConversation = async (conv) => {
    setSelectedConversation(conv);
    await fetchMessages(conv.id);
  };

  // Search users
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  // Start new conversation
  const handleStartConversation = async (recipientId) => {
    if (!initialMessage.trim()) {
      toast.error('Veuillez écrire un message');
      return;
    }

    try {
      const response = await axios.post(`${API}/conversations`, {
        recipient_id: recipientId,
        initial_message: initialMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowNewConversation(false);
      setSearchQuery('');
      setSearchResults([]);
      setInitialMessage('');
      
      // Refresh and select new conversation
      await fetchConversations();
      setSelectedConversation(response.data);
      await fetchMessages(response.data.id);
      
      toast.success('Conversation démarrée !');
    } catch (error) {
      console.error('Failed to start conversation:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la conversation');
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSendingMessage(true);
    try {
      await axios.post(`${API}/conversations/${selectedConversation.id}/messages`, {
        content: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewMessage('');
      await fetchMessages(selectedConversation.id);
      await fetchConversations();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Get other participant in conversation
  const getOtherParticipant = (conv) => {
    return conv.participants.find(p => p.id !== user?.id) || { name: 'Utilisateur' };
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Hier';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'long' });
    }
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="messages-page">
      {/* Header */}
      <div className="bg-muted/30 py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-3">
            <MessageCircle className="w-7 h-7 text-primary" />
            Messagerie
          </h1>
          <p className="text-muted-foreground mt-1">
            Échangez avec la communauté
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-250px)] min-h-[500px]">
          {/* Conversations List */}
          <Card className="md:col-span-1 flex flex-col">
            <CardHeader className="pb-3 border-b flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Conversations</CardTitle>
              <Dialog open={showNewConversation} onOpenChange={setShowNewConversation}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="new-conversation-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nouvelle conversation</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Rechercher un utilisateur..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="pl-10"
                        data-testid="user-search-input"
                      />
                    </div>
                    
                    {searching && (
                      <div className="flex justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    
                    {searchResults.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Résultats :</p>
                        {searchResults.map(result => (
                          <div 
                            key={result.id} 
                            className="p-3 border rounded-lg space-y-3"
                            data-testid={`user-result-${result.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="w-5 h-5 text-primary" />
                              </div>
                              <span className="font-medium">{result.name}</span>
                            </div>
                            <Input
                              placeholder="Votre message..."
                              value={initialMessage}
                              onChange={(e) => setInitialMessage(e.target.value)}
                              data-testid="initial-message-input"
                            />
                            <Button 
                              className="w-full" 
                              onClick={() => handleStartConversation(result.id)}
                              disabled={!initialMessage.trim()}
                              data-testid="start-conversation-btn"
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Envoyer
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Aucun utilisateur trouvé
                      </p>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="flex-1 p-0 overflow-hidden">
              <ScrollArea className="h-full">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      Aucune conversation
                    </p>
                    <Button 
                      variant="link" 
                      size="sm" 
                      onClick={() => setShowNewConversation(true)}
                    >
                      Démarrer une conversation
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y">
                    {conversations.map(conv => {
                      const other = getOtherParticipant(conv);
                      const isSelected = selectedConversation?.id === conv.id;
                      
                      return (
                        <button
                          key={conv.id}
                          onClick={() => handleSelectConversation(conv)}
                          className={`w-full p-3 text-left hover:bg-muted/50 transition-colors ${
                            isSelected ? 'bg-muted' : ''
                          }`}
                          data-testid={`conversation-${conv.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium truncate">{other.name}</span>
                                {conv.last_message && (
                                  <span className="text-xs text-muted-foreground shrink-0">
                                    {formatDate(conv.last_message.created_at)}
                                  </span>
                                )}
                              </div>
                              {conv.last_message && (
                                <p className="text-sm text-muted-foreground truncate">
                                  {conv.last_message.sender_id === user.id ? 'Vous: ' : ''}
                                  {conv.last_message.content}
                                </p>
                              )}
                            </div>
                            {conv.unread_count > 0 && (
                              <Badge variant="default" className="shrink-0">
                                {conv.unread_count}
                              </Badge>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Messages Panel */}
          <Card className="md:col-span-2 flex flex-col">
            {selectedConversation ? (
              <>
                {/* Conversation Header */}
                <CardHeader className="pb-3 border-b flex-row items-center gap-3 space-y-0">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="md:hidden"
                    onClick={() => setSelectedConversation(null)}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base">
                      {getOtherParticipant(selectedConversation).name}
                    </CardTitle>
                  </div>
                </CardHeader>

                {/* Messages */}
                <CardContent className="flex-1 p-0 overflow-hidden">
                  <ScrollArea className="h-full p-4">
                    <div className="space-y-4">
                      {messages.map((msg, index) => {
                        const isOwn = msg.sender_id === user.id;
                        const showDate = index === 0 || 
                          new Date(messages[index - 1].created_at).toDateString() !== 
                          new Date(msg.created_at).toDateString();
                        
                        return (
                          <div key={msg.id}>
                            {showDate && (
                              <div className="text-center text-xs text-muted-foreground my-4">
                                {new Date(msg.created_at).toLocaleDateString('fr-FR', {
                                  weekday: 'long',
                                  day: 'numeric',
                                  month: 'long'
                                })}
                              </div>
                            )}
                            <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[75%] ${isOwn ? 'order-2' : ''}`}>
                                <div className={`px-4 py-2 rounded-2xl ${
                                  isOwn 
                                    ? 'bg-primary text-primary-foreground rounded-br-md' 
                                    : 'bg-muted rounded-bl-md'
                                }`}>
                                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                </div>
                                <div className={`flex items-center gap-1 mt-1 text-xs text-muted-foreground ${
                                  isOwn ? 'justify-end' : ''
                                }`}>
                                  <span>
                                    {new Date(msg.created_at).toLocaleTimeString('fr-FR', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                  {isOwn && (
                                    msg.read 
                                      ? <CheckCheck className="w-3 h-3 text-primary" />
                                      : <Check className="w-3 h-3" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                </CardContent>

                {/* Message Input */}
                <div className="p-4 border-t">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Votre message..."
                      disabled={sendingMessage}
                      className="flex-1"
                      data-testid="message-input"
                    />
                    <Button 
                      type="submit" 
                      disabled={sendingMessage || !newMessage.trim()}
                      data-testid="send-message-btn"
                    >
                      {sendingMessage ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <CardContent className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">
                    Sélectionnez une conversation ou démarrez-en une nouvelle
                  </p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
